import { ItiTokenService } from "@/iti-core-react/services/iti-token-service";
import { clearAutomationFilters } from "@/pages/Automation/AutomationCards/AutomationCards";
import { ProfileDto } from "@/shared/dtos/profile-dto";
import { UserDto } from "@/shared/dtos/user-dto";
import { useAppDispatch } from "@/shared/hooks/redux-hooks";
import { AuthorizationService } from "@/shared/services/authorizationService";
import { ExperimentationService } from "@/shared/services/experimentationService";
import { artifactUploaderActions } from "@/store/artifactUploader/artifactUploaderAction";
import { resetSearchFilter } from "@/store/searchFilter/searchFilterAction";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ILogin } from "../interfaces/ILogin";
import { ItiHttpService } from "../services/iti-http-service";

interface IAuthContext {
	user?: User;
	profile?: ProfileDto;
	loading: boolean;
	signIn?: (login: ILogin, setUserInLocalStorage?: boolean) => Promise<boolean>;
	signOut?: () => void;
	setProfile?: (profile: ProfileDto) => void;
	registerUser?: (user: UserDto) => Promise<boolean>;
	setUser?: (user: User) => Promise<boolean>;
}

export class User {
	userName?: string;
	avatarUrl?: string;
	permissions: [];

	constructor(userName?: string) {
		this.userName = userName;
		this.avatarUrl = "";
		this.permissions = [];
	}

	hasPermission(permission: string): boolean {
		return permission !== "";
	}
}

export const AuthContext = React.createContext<IAuthContext>({
	loading: false,
});

const userNameKey = "userName";
const profileKey = "profile";

export const AuthProvider = (props: any) => {
	const [_user, _setUser] = useState<User>();
	const [loading, setLoading] = useState(true);
	const dispatch = useAppDispatch();
	const [profile, _setProfile] = useState<ProfileDto>(() => {
		const storedProfile = localStorage.getItem("profile");
		return storedProfile ? JSON.parse(storedProfile) : new ProfileDto();
	});

	useEffect(() => {
		(async () => {
			const userNameValue = window.localStorage.getItem(userNameKey);
			const profileValue = window.localStorage.getItem(profileKey);

			if (userNameValue) {
				_setUser(new User(userNameValue));
			}

			if (profileValue) {
				_setProfile(JSON.parse(profileValue));
			}

			setLoading(false);
		})();
	}, []);

	const setUser = useCallback((user: User) => {
		return new Promise<boolean>((resolve, reject) => {
			try {
				_setUser(user);
				localStorage.setItem(userNameKey, user.userName as string);
				ItiHttpService.composeAuthHeader();
				resolve(true);
			} catch (error) {
				reject(error);
			}
		});
	}, []);

	const setProfile = useCallback((_profile: ProfileDto) => {
		_setProfile(_profile);
		if (_profile) {
			localStorage.setItem(profileKey, JSON.stringify(_profile));
		}
	}, []);

	const signIn = useCallback(
		async (login: ILogin) => {
			try {
				return await AuthorizationService.login(login).then(
					async (result: boolean) => {
						return result ? setUser(new User(login.UserName)) : null;
					},
				);
			} catch {
				return false;
			}
		},
		[setUser],
	);

	const signOut = useCallback(async () => {
		await ExperimentationService.stopExperimentationFromSession();
		_setUser(undefined);
		_setProfile(new ProfileDto());
		AuthorizationService.logout();
		window.localStorage.removeItem(userNameKey);
		window.localStorage.removeItem(profileKey);
		window.localStorage.removeItem("PaginationPageIndex");
		window.localStorage.removeItem("PaginationPageSize");
		clearAutomationFilters();
		window.sessionStorage.clear();
		dispatch(resetSearchFilter());
		dispatch(artifactUploaderActions.clearAllUploads());
	}, [dispatch]);

	const registerUser = useCallback(
		async (newUser: UserDto) => {
			ItiTokenService.clearToken();
			const result = await AuthorizationService.registerUser(newUser);
			if (result) {
				setUser(new User(newUser.email));
			}
			return result;
		},
		[setUser],
	);

	const user = useMemo(() => {
		if (ItiTokenService.areTokensExpired()) {
			signOut();
			return undefined;
		}
		if (_user && !profile) {
			return undefined;
		}
		return _user;
	}, [_user, profile, signOut]);

	return (
		<AuthContext.Provider
			value={{
				user,
				signIn,
				signOut,
				profile,
				setProfile,
				registerUser,
				loading,
				setUser,
			}}
			{...props}
		/>
	);
};
